import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BackendService } from './general/backend.service';
import {
  StatementGenerationRequest,
  StatementGenerationResponse,
  StatementProcess,
  GeneratedStatement,
  SurveyInputData,
  PDFInputData,
  PubMedInputData,
  SurveyListItem,
  SurveyResults,
  GradeRevisionListItem,
  GradeDocument,
  ProcessStatusResponse
} from '../models/statement.models';
import {
  StatementGenerationRequest as NewStatementGenerationRequest,
  GenerationInitResponse,
  GenerationStatusResponse,
  StatementsResponse,
  MarkInSurveyRequest,
  UnmarkFromSurveyRequest,
  MarkResponse,
  GeneratedStatement as DraftGeneratedStatement,
  CreateSurveyFromStatementsRequest,
  CreateSurveyFromStatementsResponse
} from '../models/statement-generator.models';
import { Observable, interval, Subject } from 'rxjs';
import { takeWhile, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StatementGeneratorService {

  constructor(
    private backend: BackendService,
    private http: HttpClient
  ) { }

  /**
   * Generate statements from multiple input sources
   */
  generateStatements(
    request: StatementGenerationRequest,
    successCallback: (response: StatementGenerationResponse) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post('statements/generate', request).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get status of statement generation process
   */
  getProcessStatus(
    processId: string,
    successCallback: (process: StatementProcess) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`statements/process/${processId}/status`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get generated statements from a completed process
   */
  getGeneratedStatements(
    processId: string,
    successCallback: (statements: GeneratedStatement[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`statements/process/${processId}/statements`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Poll process status until completion
   */
  pollProcessStatus(
    processId: string,
    pollingInterval: number = 3000,
    maxAttempts: number = 60
  ): Observable<StatementProcess> {
    let attempts = 0;
    let isComplete = false;
    const statusSubject = new Subject<StatementProcess>();

    const polling = interval(pollingInterval).pipe(
      takeWhile(() => attempts < maxAttempts && !isComplete),
      switchMap(() => {
        attempts++;
        return new Observable<StatementProcess>(observer => {
          this.getProcessStatus(
            processId,
            (process) => {
              observer.next(process);
              if (process.status === 'completed' || process.status === 'failed') {
                isComplete = true;
                observer.complete();
              }
            },
            (error) => observer.error(error)
          );
        });
      })
    ).subscribe({
      next: (process) => {
        statusSubject.next(process);
        if (process.status === 'completed' || process.status === 'failed') {
          statusSubject.complete();
        }
      },
      error: (error) => {
        statusSubject.error(error);
        statusSubject.complete();
      },
      complete: () => statusSubject.complete()
    });

    return statusSubject.asObservable();
  }

  /**
   * Get surveys for a project (completed/closed only)
   */
  getProjectSurveys(
    projectId: number,
    successCallback: (surveys: SurveyListItem[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/surveys`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get survey responses and results (detailed view)
   */
  getSurveyResults(
    surveyId: number,
    successCallback: (data: SurveyResults) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`surveys/${surveyId}/results`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get GRADE revisions for a project (completed only)
   */
  getProjectGradeRevisions(
    projectId: number,
    successCallback: (revisions: GradeRevisionListItem[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/grade-revisions`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get GRADE revision documents (detailed view)
   */
  getGradeRevisionDocuments(
    revisionId: number,
    successCallback: (documents: GradeDocument[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`grade-revisions/${revisionId}/documents`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Upload PDF document for statement generation
   */
  uploadPDFDocument(
    file: File,
    projectId: number,
    successCallback: (data: PDFInputData) => void,
    errorCallback: (error: any) => void
  ): void {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId.toString());

    // Use BackendService postFormData for multipart upload
    this.backend.postFormData('statements/upload-pdf', formData).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Fetch PubMed article by PMID
   */
  fetchPubMedArticle(
    pmid: string,
    successCallback: (data: PubMedInputData) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post('tools/pubmed/fetch', { pmid }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Search PubMed articles
   */
  searchPubMed(
    query: string,
    page: number = 1,
    successCallback: (results: any) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post('tools/pubmed/search', { queryString: query, currentPage: page }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get literature review documents
   */
  getLiteratureReviewDocuments(
    gradeRevisionId: number,
    successCallback: (documents: any[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`grade-revisions/${gradeRevisionId}/documents`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get all draft survey statements for a project (Lambda-generated)
   */
  getProjectDraftStatements(
    projectId: number,
    successCallback: (result: { statements: DraftGeneratedStatement[] }) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/draft-statements`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get stored statements for a project
   */
  getProjectStatements(
    projectId: number,
    successCallback: (statements: GeneratedStatement[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/statements`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Save generated statements to project
   */
  saveStatementsToProject(
    projectId: number,
    statements: GeneratedStatement[],
    successCallback: (result: any) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post(`projects/${projectId}/statements`, { statements }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Update a statement
   */
  updateStatement(
    statementId: number,
    updates: Partial<GeneratedStatement>,
    successCallback: (statement: GeneratedStatement) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.put(`statements/${statementId}`, updates).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Update a statement with AI assistance
   */
  updateStatementWithAI(
    statementId: number,
    updatePrompt: string,
    successCallback: (statement: GeneratedStatement) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post(`statements/${statementId}/update-with-ai`, { prompt: updatePrompt }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Delete a statement
   */
  deleteStatement(
    statementId: number,
    successCallback: (result: any) => void,
    errorCallback: (error: any) => void
  ): void {
    // Use HttpClient directly for proper DELETE method
    // Backend expects: DELETE /api/statements/{id}
    this.http.delete(`${environment.apiUrl}statements/${statementId}`, {
      withCredentials: true
    }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Delete an entire statement generation (process and all its statements)
   */
  deleteStatementGeneration(
    processId: string,
    successCallback: (result: any) => void,
    errorCallback: (error: any) => void
  ): void {
    // Use HttpClient directly for proper DELETE method
    // Backend expects: DELETE /api/statements/process/{processId}
    this.http.delete(`${environment.apiUrl}statements/process/${processId}`, {
      withCredentials: true
    }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get all statement generations for a project (history/list)
   */
  getProjectGenerations(
    projectId: number,
    successCallback: (generations: any[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/statement-generations`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get the latest (most recent) generation for a project
   */
  getLatestGeneration(
    projectId: number,
    successCallback: (generation: any) => void,
    errorCallback: (error: any) => void
  ): void {
    // Try to get from dedicated endpoint first
    this.backend.get(`projects/${projectId}/statement-generations/latest`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => {
        // Fallback: Get all generations and return the first one (most recent)
        if (error?.status === 404) {
          console.log('Latest generation endpoint not found, using fallback...');
          this.getProjectGenerations(
            projectId,
            (generations: any[]) => {
              if (generations && generations.length > 0) {
                // Return the most recent one (first in array, assuming sorted by date desc)
                successCallback(generations[0]);
              } else {
                // No generations exist
                successCallback(null);
              }
            },
            errorCallback
          );
        } else {
          errorCallback(error);
        }
      }
    });
  }

  /**
   * Get details of a specific generation including statements
   */
  getGenerationDetails(
    processId: string,
    successCallback: (details: any) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`statements/process/${processId}/details`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Refresh generation status (for ongoing processes)
   */
  refreshGenerationStatus(
    processId: string,
    successCallback: (status: any) => void,
    errorCallback: (error: any) => void
  ): void {
    this.getProcessStatus(processId, successCallback, errorCallback);
  }

  /**
   * Get stored PDF documents for a project
   */
  getProjectPDFDocuments(
    projectId: number,
    successCallback: (pdfs: any[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/pdf-documents`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get stored PubMed documents for a project
   */
  getProjectPubMedDocuments(
    projectId: number,
    successCallback: (documents: any[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/pubmed-documents`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Save a PubMed document to a project
   */
  savePubMedDocumentToProject(
    projectId: number,
    pubmedData: any,
    successCallback: (result: any) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post(`projects/${projectId}/pubmed-documents`, pubmedData).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  // ============================================================================
  // NEW DRAFT SURVEY STATEMENT GENERATOR METHODS (AWS Lambda-based)
  // ============================================================================

  /**
   * Initiate a new statement generation process using the new AWS Lambda endpoint
   */
  initiateStatementGeneration(
    request: NewStatementGenerationRequest,
    successCallback: (response: GenerationInitResponse) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post('statements/statements-generator', request).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Check the status of a statement generation process
   */
  checkGenerationStatus(
    processId: string,
    successCallback: (status: GenerationStatusResponse) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`statements/statements-generator/${processId}/status`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get generated statements from a completed process
   */
  getGeneratedStatementsNew(
    processId: string,
    successCallback: (response: StatementsResponse) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`statements/statements-generator/${processId}/statements`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Mark statements as used in a survey
   */
  markStatementsInSurvey(
    request: MarkInSurveyRequest,
    successCallback: (response: MarkResponse) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post('statements/statements-generator/mark-in-survey', request).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Unmark statements from a survey
   */
  unmarkStatementsFromSurvey(
    request: UnmarkFromSurveyRequest,
    successCallback: (response: MarkResponse) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post('statements/statements-generator/unmark-from-survey', request).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Create a new DRAFT survey from selected statements
   */
  createSurveyFromStatements(
    request: CreateSurveyFromStatementsRequest,
    successCallback: (response: CreateSurveyFromStatementsResponse) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post('statements/statements-generator/create-survey-from-statements', request).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Rewrite a survey question statement asynchronously.
   *
   * The backend dispatches a job that runs all data prep + Lambda invocation,
   * then writes the result to `statement_rewrite_processes`. This method:
   *   1. POSTs to enqueue the job (returns a process_id).
   *   2. Polls the status endpoint with exponential backoff + jitter.
   *   3. Invokes successCallback with `{rewritten_statement}` on `completed`,
   *      or errorCallback on `failed` / HTTP error / timeout.
   *
   * Callback signature is preserved so callers don't need to know about polling.
   */
  rewriteStatement(
    questionId: number,
    surveyId: number,
    successCallback: (result: { rewritten_statement: string }) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post('statements/statements-generator/rewrite-statement', {
      question_id: questionId,
      survey_id: surveyId
    }).subscribe({
      next: (response: any) => {
        const processId = response?.process_id;
        if (!processId) {
          errorCallback(new Error('No process_id returned from rewrite dispatch'));
          return;
        }
        this.pollRewriteStatus(processId, successCallback, errorCallback);
      },
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Poll `rewrite-statement/{processId}/status` until terminal.
   * Schedule: 1.5s, 2.25s, 3.4s, ..., capped at 10s. ±20% jitter.
   * Gives up after maxDurationMs (default 5 min) to bound runaway polling.
   */
  private pollRewriteStatus(
    processId: string,
    successCallback: (result: { rewritten_statement: string }) => void,
    errorCallback: (error: any) => void,
    initialIntervalMs: number = 1500,
    maxIntervalMs: number = 10000,
    backoffFactor: number = 1.5,
    maxDurationMs: number = 5 * 60 * 1000
  ): void {
    const startedAt = Date.now();
    let currentDelay = initialIntervalMs;
    let stopped = false;

    const tick = () => {
      if (stopped) return;

      if (Date.now() - startedAt >= maxDurationMs) {
        errorCallback(new Error('Rewrite polling timed out'));
        return;
      }

      this.backend.get(`statements/statements-generator/rewrite-statement/${processId}/status`).subscribe({
        next: (status: any) => {
          if (stopped) return;
          if (status?.status === 'completed') {
            stopped = true;
            successCallback({ rewritten_statement: status.rewritten_statement });
            return;
          }
          if (status?.status === 'failed') {
            stopped = true;
            errorCallback(new Error(status.error_message || 'Rewrite failed'));
            return;
          }
          // queued / processing — schedule next poll.
          currentDelay = Math.min(currentDelay * backoffFactor, maxIntervalMs);
          const jittered = currentDelay * (0.8 + Math.random() * 0.4);
          setTimeout(tick, jittered);
        },
        error: (error) => {
          stopped = true;
          errorCallback(error);
        }
      });
    };

    // First poll uses the initial delay (no jitter) — keeps early UX snappy.
    setTimeout(tick, initialIntervalMs);
  }

  /**
   * Poll generation status until completion (new endpoint)
   * Returns an Observable that emits status updates
   */
  pollGenerationStatus(
    processId: string,
    pollingInterval: number = 2500,
    maxAttempts: number = 280
  ): Observable<GenerationStatusResponse> {
    let attempts = 0;
    let isComplete = false;
    const statusSubject = new Subject<GenerationStatusResponse>();

    const polling = interval(pollingInterval).pipe(
      takeWhile(() => attempts < maxAttempts && !isComplete),
      switchMap(() => {
        attempts++;
        return new Observable<GenerationStatusResponse>(observer => {
          this.checkGenerationStatus(
            processId,
            (status) => {
              observer.next(status);
              if (status.status === 'completed' || status.status === 'failed') {
                isComplete = true;
                observer.complete();
              }
            },
            (error) => observer.error(error)
          );
        });
      })
    ).subscribe({
      next: (status) => {
        statusSubject.next(status);
        if (status.status === 'completed' || status.status === 'failed') {
          statusSubject.complete();
        }
      },
      error: (error) => {
        statusSubject.error(error);
        statusSubject.complete();
      },
      complete: () => statusSubject.complete()
    });

    return statusSubject.asObservable();
  }
}
